import PlaylistPlayIcon from '@mui/icons-material/PlaylistPlay';
import { Box, IconButton, Typography } from '@mui/material';
import { AnimatePresence, LayoutGroup, motion } from 'framer-motion';
import { createPortal } from 'react-dom';
import { forwardRef, memo, useCallback, useEffect, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { useArtistTheme } from '../../../contexts/ArtistThemeContext';
import { usePlaylist } from '../../../contexts/PlaylistContext';
import useSearchYouTube from '../../../hooks/useSearchYoutube';
import useTourDates from '../../../hooks/useTourDates';
import useWindowSize from '../../../hooks/useWindowSize';
import { clearFrontendGeneratedCaches } from '../../../utils/cacheReset';
import { BREAKPOINT, getArtistPath, lc, removeHyphens, trackClick, trackInteraction } from '../../_helpers';
import TruevinedSVGLogo from '../../truevinedSVGLogo';
import { FullscreenMenu, type FullscreenMenuItem } from './FullscreenMenu';

export const AnimatedEqualizer = memo(
  ({ isPlaying, size = 'md' }: { isPlaying: boolean; size?: 'xs' | 'md' }) => {
    const barCount = 4;
    const heights = size === 'xs' ? [10, 10, 10, 10] : [7, 7, 7, 7];
    const maxHeight = size === 'xs' ? 14 : 18;
    const barWidth = size === 'xs' ? 2 : 3;
    const gap = size === 'xs' ? 3 : 2;

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'center',
          height: size === 'xs' ? 5 : 8,
          gap: `${gap}px`,
          mt: {
            xs: '5px',
            md: '0px',
          },
        }}
      >
        {Array.from({ length: barCount }).map((_, i) => (
          <Box
            key={i}
            sx={{
              width: barWidth,
              backgroundColor: 'var(--logoColor)',
              borderRadius: '1px',
              height: isPlaying ? undefined : `${heights[i] * 0.4}px`,
              maxHeight: size === 'xs' ? 10 : 14,
              animation: isPlaying ? `equalizer-bar-${i} 0.${4 + i}s ease-in-out infinite` : 'none',
              '@keyframes equalizer-bar-0': {
                '0%, 100%': { height: `${heights[0]}px` },
                '50%': { height: `${maxHeight}px` },
              },
              '@keyframes equalizer-bar-1': {
                '0%, 100%': { height: `${maxHeight}px` },
                '50%': { height: `${heights[1] * 0.5}px` },
              },
              '@keyframes equalizer-bar-2': {
                '0%, 100%': { height: `${heights[2]}px` },
                '50%': { height: `${maxHeight * 0.9}px` },
              },
              '@keyframes equalizer-bar-3': {
                '0%, 100%': { height: `${maxHeight * 0.8}px` },
                '50%': { height: `${heights[3] * 0.4}px` },
              },
            }}
          />
        ))}
      </Box>
    );
  }
);

interface MenuToggleProps {
  isInFullscreen?: boolean;
  showMenu: boolean;
  onClick: () => void;
}

const fullscreenControlChrome = {
  background: 'color-mix(in srgb, var(--color5) 48%, transparent)',
  border: '1px solid color-mix(in srgb, var(--logoColor) 20%, transparent)',
  boxShadow: 'none',
  backdropFilter: 'blur(18px) saturate(1.18)',
  WebkitBackdropFilter: 'blur(18px) saturate(1.18)',
};

// Bar positions for the closed hamburger, measured from the vertical centre of
// the button. The toggle is a real <button>, which centres its content, so the
// bars are pinned with `top: 50%` instead of relying on where a button puts an
// absolutely positioned child with no `top` (that dropped the icon below the
// playlist button next to it).
const menuBarOffsets = {
  closed: { xs: '0.45rem', md: '0.22rem' },
  hover: { xs: '0.6rem', md: '0.32rem' },
  shortShift: { xs: '0.75rem', md: '0.4rem' },
};

const barTransform = (bar: 'top' | 'bottom', state: 'closed' | 'hover') => ({
  xs:
    bar === 'top'
      ? `translate(0, calc(-50% - ${menuBarOffsets[state].xs}))`
      : `translate(${menuBarOffsets.shortShift.xs}, calc(-50% + ${menuBarOffsets[state].xs})) scaleX(70%)`,
  md:
    bar === 'top'
      ? `translate(0, calc(-50% - ${menuBarOffsets[state].md}))`
      : `translate(${menuBarOffsets.shortShift.md}, calc(-50% + ${menuBarOffsets[state].md})) scaleX(70%)`,
});

const MenuToggle = memo(forwardRef<HTMLButtonElement, MenuToggleProps>(({ isInFullscreen = false, showMenu, onClick }, ref) => {
  const openTransform = (angle: number) =>
    isInFullscreen ? `translate(-50%, -50%) rotate(${angle}deg)` : `translate(0, -50%) rotate(${angle}deg)`;
  const barBase = {
    content: '""',
    position: 'absolute',
    top: '50%',
    left: isInFullscreen ? '50%' : 0,
    width: isInFullscreen ? '18px' : '100%',
    height: {
      xs: isInFullscreen ? '2px' : '0.35rem',
      md: isInFullscreen ? '1.5px' : '0.125rem',
    },
    background: isInFullscreen ? 'var(--logoColor2)' : 'var(--logoColor)',
    transition: 'transform 0.3s ease-in-out',
  };
  const barState = (bar: 'top' | 'bottom', state: 'closed' | 'hover') =>
    showMenu ? openTransform(bar === 'top' ? 45 : -45) : barTransform(bar, state);

  return (
    <Box
      component="button"
      type="button"
      ref={ref}
      aria-label={showMenu ? 'Close navigation menu' : 'Open navigation menu'}
      aria-expanded={showMenu}
      aria-haspopup="dialog"
      sx={{
        appearance: 'none',
        display: 'block',
        padding: 0,
        margin: 0,
        border: 0,
        background: 'transparent',
        color: 'inherit',
        font: 'inherit',
        width: {
          xs: isInFullscreen ? '88px' : '5rem',
          md: isInFullscreen ? '78px' : '2.8125rem',
        },
        height: {
          xs: isInFullscreen ? '44px' : '5rem',
          md: isInFullscreen ? '44px' : '2.8125rem',
        },
        position: 'relative',
        cursor: 'pointer',
        zIndex: '2',
        borderRadius: isInFullscreen ? '999px' : 0,
        '&:focus-visible': { outline: '2px solid var(--logoColor2)', outlineOffset: '4px' },
        ...(isInFullscreen ? fullscreenControlChrome : {}),
        transition:
          'transform .3s ease-in-out, border-color .2s ease, background .2s ease, box-shadow .2s ease',
        '::before': { ...barBase, transform: barState('top', 'closed') },
        '::after': { ...barBase, transform: barState('bottom', 'closed') },
        '&:hover': {
          ...(isInFullscreen
            ? {
                borderColor: 'color-mix(in srgb, var(--logoColor2) 44%, transparent)',
                boxShadow: 'none',
              }
            : {}),
          '::before': { transform: barState('top', 'hover') },
          '::after': { transform: barState('bottom', 'hover') },
        },
      }}
      className={'MenuIcon' + (showMenu ? ' active' : '')}
      onClick={onClick}
    />
  );
}));

interface ArtistNavProps {
  artist?: string;
  artistImage?: string;
}
export function ArtistNav(props: ArtistNavProps) {
  const [showMenu, setShowMenu] = useState(false);
  const [showMobileArtistName, setShowMobileArtistName] = useState(false);
  const [cacheClearState, setCacheClearState] = useState<'idle' | 'clearing' | 'cleared'>('idle');
  const cacheClearHoldTimer = useRef<number | null>(null);
  const { togglePanel, playlist, isPlaying, isPanelOpen, togglePlayerModal } = usePlaylist();
  const { variants: themeVariants, selectedThemeId, setSelectedThemeId } = useArtistTheme();
  const windowSize = useWindowSize();
  const reallyIsMobile = isMobile || windowSize.width <= BREAKPOINT;
  const location = useLocation();
  const navigate = useNavigate();
  const artistPath = getArtistPath(props.artist);
  const isHomePage = location.pathname === artistPath;
  const membersPath = getArtistPath(props.artist, '/branches');
  const hasMembers = lc(props.artist) === 'truevined';
  const merchPath = '/merch';
  const hasMerch = lc(props.artist) === 'truevined';
  const gamesPath = '/games';
  const hasGames = lc(props.artist) === 'truevined';
  const isGamesPage = location.pathname === gamesPath || location.pathname.startsWith(`${gamesPath}/`);
  const isMerchPage = location.pathname === merchPath || location.pathname.startsWith(`${merchPath}/`);
  const bioPath = getArtistPath(props.artist, '/bio');
  const isBioPage = location.pathname === bioPath;
  const isMembersPage = location.pathname === membersPath || location.pathname.startsWith(`${membersPath}/`);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleScroll = () => {
      const scrollY = window.scrollY;
      const threshold = reallyIsMobile ? (isHomePage ? 400 : -40) : 300;
      setShowMobileArtistName(scrollY > threshold);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [reallyIsMobile, isHomePage]);

  // Check which sections have data
  const { data: tourData } = useTourDates(props.artist || '', [props.artist]);
  const { data: videos, isLoading: videosLoading } = useSearchYouTube(
    { artistName: props.artist || '', songName: '' },
    { maxResults: 1 },
    [props.artist]
  );

  const hasBio = Boolean(props.artist);
  const hasTourDates = tourData && tourData.events && tourData.events.length > 0;
  const hasVideos = videosLoading || (videos && videos.length > 0 && videos[0]?.id?.videoId);
  const fullscreenMenuItems = [
    { label: 'MUSIC', section: 'music-section', show: true, link: null },
    { label: 'ABOUT', section: null, show: hasBio, link: bioPath },
    { label: 'TOUR', section: 'TourDatesSection', show: hasTourDates, link: null },
    { label: 'VIDEOS', section: 'VideosSection', show: hasVideos, link: null },
    { label: 'GALLERY', section: 'GallerySection', show: true, link: null },
    { label: 'CONTACT', section: 'ContactSection', show: true, link: null },
    { label: 'MERCH', section: null, show: hasMerch, link: merchPath },
    { label: 'GAMES', section: null, show: hasGames, link: gamesPath },
    { label: 'BRANCHES', section: null, show: hasMembers, link: membersPath },
  ].filter((item) => item.show);

  const menuToggleRef = useRef<HTMLButtonElement | null>(null);
  const menuWasOpen = useRef(false);

  const handleMenuClick = useCallback(() => {
    setShowMenu((prev) => {
      trackClick(prev ? 'nav-menu-close' : 'nav-menu-open', { label: 'Fullscreen menu toggle', section: 'nav' });
      return !prev;
    });
  }, []);

  const closeMenu = useCallback(() => {
    trackClick('nav-menu-close', { label: 'Fullscreen menu toggle', section: 'nav' });
    setShowMenu(false);
  }, []);

  useEffect(() => {
    if (showMenu) {
      menuWasOpen.current = true;
      return;
    }
    if (!menuWasOpen.current) return;
    menuWasOpen.current = false;
    const frame = window.requestAnimationFrame(() => menuToggleRef.current?.focus({ preventScroll: true }));
    return () => window.cancelAnimationFrame(frame);
  }, [showMenu]);

  const handleLogoClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      trackClick('nav-logo', { label: 'Site logo', section: 'nav', href: artistPath });
      if (isHomePage) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        navigate(artistPath);
      }
    },
    [isHomePage, artistPath, navigate]
  );

  const sectionHref = (sectionClass: string) =>
    isHomePage ? `#${sectionClass}` : `${artistPath}#${sectionClass}`;

  const handleSectionClick = (sectionClass: string) => (event: React.MouseEvent) => {
    event.preventDefault();
    scrollToSection(sectionClass);
  };

  const scrollToSection = (sectionClass: string) => {
    trackInteraction(`nav-click-${sectionClass}`, sectionClass);
    trackClick(`nav:${sectionClass}`, { label: `Nav ${sectionClass}`, section: 'nav' });

    if (isHomePage) {
      const section = document.querySelector(`.${sectionClass}`);
      if (section) {
        setShowMenu(false);
        window.requestAnimationFrame(() => {
          const offset = 100;
          const elementPosition = section.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - offset;
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth',
          });
        });
      }
    } else {
      navigate(`${artistPath}#${sectionClass}`);
      setShowMenu(false);
    }
  };

  const fullscreenMenuLinks: FullscreenMenuItem[] = fullscreenMenuItems.map((item) => ({
    label: item.label,
    href: item.link ?? sectionHref(item.section as string),
    opensPage: Boolean(item.link),
    onSelect: (event) => {
      event.preventDefault();
      if (item.link) {
        trackClick(`nav-menu:${item.label.toLowerCase()}`, { label: `Menu ${item.label}`, section: 'nav-menu', href: item.link });
        setShowMenu(false);
        navigate(item.link);
      } else if (item.section) {
        scrollToSection(item.section);
      }
    },
  }));

  const handleClearCaches = useCallback(async () => {
    if (cacheClearState === 'clearing') return;

    setCacheClearState('clearing');
    try {
      await clearFrontendGeneratedCaches();
      trackInteraction('clear-frontend-cache', 'fullscreen-menu');
      setCacheClearState('cleared');
      window.setTimeout(() => {
        window.location.reload();
      }, 500);
    } catch {
      setCacheClearState('idle');
    }
  }, [cacheClearState]);

  const cancelCacheClearHold = useCallback(() => {
    if (cacheClearHoldTimer.current) {
      window.clearTimeout(cacheClearHoldTimer.current);
      cacheClearHoldTimer.current = null;
    }
  }, []);

  const armCacheClearHold = useCallback(() => {
    cancelCacheClearHold();
    cacheClearHoldTimer.current = window.setTimeout(() => {
      cacheClearHoldTimer.current = null;
      handleClearCaches();
    }, 900);
  }, [cancelCacheClearHold, handleClearCaches]);

  useEffect(() => cancelCacheClearHold, [cancelCacheClearHold]);

  useEffect(() => {
    if (isHomePage && location.hash) {
      const sectionClass = location.hash.replace('#', '');
      setTimeout(() => {
        const section = document.querySelector(`.${sectionClass}`);
        if (section) {
          const offset = 100;
          const elementPosition = section.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - offset;
          window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
        }
      }, 100);
    }
  }, [location.hash, isHomePage]);

  const linkStyle = {
    fontSize: '1.2rem',
    color: 'var(--logoColor)',
    textDecoration: 'none',
    cursor: 'pointer',
    '&:hover': { color: 'var(--logoColor)' },
  };

  const navTree = (
    <LayoutGroup>
      <Box
        sx={{
          width: '100%',
          height: { xs: '9.8rem', md: '6.3rem' },
          padding: '1.42rem 2.77rem',
          left: 0,
          top: 0,
          position: 'fixed',
          justifyContent: { xs: 'flex-end', md: 'space-between' },
          alignItems: 'center',
          gap: '2.67rem',
          display: 'flex',
          zIndex: 9999,
          backgroundColor: 'color-mix(in srgb, var(--backgroundColor) 58%, transparent)',
          backdropFilter: 'blur(14px)',
        }}
        className="Navigation"
      >
        {/* Mobile Artist Name - shows when scrolled past avatar */}
        <AnimatePresence>
          {showMobileArtistName && reallyIsMobile && (
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              style={{
                position: 'absolute',
                left: '2rem',
              }}
            >
              <Box onClick={handleLogoClick} sx={{ textDecoration: 'none', cursor: 'pointer' }}>
                {lc(props.artist) === 'truevined' ? (
                  <TruevinedSVGLogo color="var(--logoColor)" width={180} alt="Truevined" />
                ) : (
                  <Typography
                    className={`artist-name mobile ${removeHyphens(props.artist)}`}
                    sx={{
                      color: 'var(--logoColor)',
                      fontSize: { xs: '4.5rem', md: '3.5rem' },
                      fontFamily: 'Covered By Your Grace',
                      fontWeight: '400',
                      overflowWrap: 'break-word',
                    }}
                  >
                    {props.artist}
                  </Typography>
                )}
              </Box>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Desktop Artist Name */}
        <Box
          onClick={handleLogoClick}
          sx={{
            display: { xs: 'none', md: 'flex' },
            justifyContent: 'flex-start',
            alignItems: 'center',
            gap: '0.48rem',
            textDecoration: 'none',
            cursor: 'pointer',
            transition: 'opacity 0.2s ease',
            '&:hover': { opacity: 0.8 },
          }}
          className="LogoWrapper"
        >
          {lc(props.artist) === 'truevined' ? (
            <TruevinedSVGLogo color="var(--logoColor)" width={160} alt="Truevined" />
          ) : (
            <Typography
              className={`artist-name ${removeHyphens(props.artist)}`}
              sx={{
                color: 'var(--logoColor)',
                fontSize: '2.5rem',
                fontFamily: 'Covered By Your Grace',
                fontWeight: '400',
                overflowWrap: 'break-word',
              }}
            >
              {props.artist}
            </Typography>
          )}
        </Box>

        <Box
          sx={{
            display: { xs: 'none', md: 'flex' },
            height: '1.11rem',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '3rem',
            color: 'var(--textColor)',
            fontSize: '.91rem',
            letterSpacing: '0.035rem',
            fontStyle: 'normal',
            fontFamily: 'Poppins',
            fontWeight: '400',
            overflowWrap: 'break-word',
          }}
          className="HeaderNav"
        >
          <Typography
            component={RouterLink}
            to={sectionHref('music-section')}
            onClick={handleSectionClick('music-section')}
            className="HeaderNav-item Music"
            sx={linkStyle}
          >
            MUSIC
          </Typography>
          {/* {hasBio && (
            <Link
              component={RouterLink}
              to={getArtistPath(props.artist, "/bio")}
              className="HeaderNav-item Bio"
              sx={{ ...linkStyle, textDecoration: "none" }}
            >
              BIO
            </Link>
          )} */}
          {hasBio && (
            <Typography
              component={RouterLink}
              to={bioPath}
              onClick={() => trackClick('nav:bio', { label: 'Nav about', section: 'nav', href: bioPath })}
              className="HeaderNav-item About"
              aria-current={isBioPage ? 'page' : undefined}
              sx={{ ...linkStyle, color: isBioPage ? 'var(--logoColor2)' : 'var(--logoColor)' }}
            >
              ABOUT
            </Typography>
          )}
          {hasTourDates && (
            <Typography
              component={RouterLink}
              to={sectionHref('TourDatesSection')}
              onClick={handleSectionClick('TourDatesSection')}
              className="HeaderNav-item Tour"
              sx={linkStyle}
            >
              TOUR
            </Typography>
          )}
          {hasMerch && (
            <Typography
              component={RouterLink}
              to={merchPath}
              onClick={() => trackClick('nav:merch', { label: 'Nav merch', section: 'nav', href: merchPath })}
              className="HeaderNav-item Merch"
              aria-current={isMerchPage ? 'page' : undefined}
              sx={{
                ...linkStyle,
                color: isMerchPage ? 'var(--logoColor2)' : 'var(--logoColor)',
                '&:hover': { color: 'var(--logoColor2)' },
              }}
            >
              MERCH
            </Typography>
          )}
          {hasGames && (
            <Typography
              component={RouterLink}
              to={gamesPath}
              onClick={() => trackClick('nav:games', { label: 'Nav games', section: 'nav', href: gamesPath })}
              className="HeaderNav-item Games"
              aria-current={isGamesPage ? 'page' : undefined}
              sx={{
                ...linkStyle,
                color: isGamesPage ? 'var(--logoColor2)' : 'var(--logoColor)',
                '&:hover': { color: 'var(--logoColor2)' },
              }}
            >
              GAMES
            </Typography>
          )}
          {hasMembers && (
            <Typography
              component={RouterLink}
              to={membersPath}
              className="HeaderNav-item Members"
              aria-current={isMembersPage ? 'page' : undefined}
              sx={{
                ...linkStyle,
                color: isMembersPage ? 'var(--logoColor2)' : 'var(--logoColor)',
                '&:hover': { color: 'var(--logoColor2)' },
              }}
            >
              BRANCHES
            </Typography>
          )}
        </Box>

        {/* Right Side Controls Wrapper */}
        <Box
          className="RightControls"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: '1.5rem', md: '2rem' },
          }}
        >
          {/* Music Playing Indicator - Equalizer with animated bars (click to toggle play/pause) */}
          <AnimatePresence>
            {(isPlaying || playlist.length > 0) && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: 0.2 }}
              >
                <Box
                  onClick={() => {
                    trackClick('nav-open-player', { label: 'Open music player', section: 'nav' });
                    togglePlayerModal();
                  }}
                  role="button"
                  tabIndex={0}
                  aria-label="Open music player"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      togglePlayerModal();
                    }
                  }}
                  sx={{
                    cursor: 'pointer',
                    padding: { xs: '0.5rem', md: '0.25rem' },
                    // mr: reallyIsMobile ? '0.25rem' : '.5rem',
                    borderRadius: '4px',
                    transition: 'background-color 0.2s ease',
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.1)',
                    },
                  }}
                >
                  <AnimatedEqualizer isPlaying={isPlaying} size={reallyIsMobile ? 'xs' : 'md'} />
                </Box>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Playlist Toggle Button */}
          <IconButton
            onClick={() => {
              trackClick(isPanelOpen ? 'nav-playlist-close' : 'nav-playlist-open', { label: 'Playlist panel toggle', section: 'nav' });
              togglePanel();
            }}
            aria-label={isPanelOpen ? 'Close playlist' : 'Open playlist'}
            sx={{
              color: 'var(--logoColor)',
              position: 'relative',
              padding: { xs: '1.25rem', md: '0.5rem' },
              minWidth: { xs: '5rem', md: 'auto' },
              minHeight: { xs: '5rem', md: 'auto' },
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.1)',
              },
            }}
          >
            <PlaylistPlayIcon
              aria-hidden="true"
              sx={{ fontSize: { xs: '4.5rem', md: '1.75rem' } }}
            />
            {playlist.length > 0 && (
              <Box
                sx={{
                  position: 'absolute',
                  top: { xs: '0.25rem', md: '0.3125rem' },
                  right: { xs: '0.25rem', md: '0.3125rem' },
                  backgroundColor: 'var(--logoColor2)',
                  color: 'var(--color5)',
                  borderRadius: '50%',
                  width: { xs: '2rem', md: '1.125rem' },
                  height: { xs: '2rem', md: '1.125rem' },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: { xs: '1.1rem', md: '0.65rem' },
                  fontWeight: 700,
                }}
              >
                {playlist.length}
              </Box>
            )}
          </IconButton>

          {/* Menu Toggle - Only show when menu is closed (will animate to fullscreen position) */}
          {!showMenu && (
            <motion.div
              layoutId="menu-toggle"
              transition={{ type: 'spring', stiffness: 400, damping: 30 }}
            >
              <MenuToggle ref={menuToggleRef} showMenu={showMenu} onClick={handleMenuClick} />
            </motion.div>
          )}
        </Box>

        <AnimatePresence>
          {showMenu && (
            <FullscreenMenu
              artist={props.artist}
              artistImage={props.artistImage}
              items={fullscreenMenuLinks}
              themes={themeVariants}
              selectedThemeId={selectedThemeId}
              onSelectTheme={setSelectedThemeId}
              onClose={closeMenu}
              compact={reallyIsMobile}
              cacheState={cacheClearState}
              onCacheHoldStart={armCacheClearHold}
              onCacheHoldEnd={cancelCacheClearHold}
              onCacheClear={handleClearCaches}
              closeControl={
                <motion.div layoutId="menu-toggle" transition={{ type: 'spring', stiffness: 400, damping: 30 }}>
                  <MenuToggle isInFullscreen showMenu={showMenu} onClick={handleMenuClick} />
                </motion.div>
              }
            />
          )}
        </AnimatePresence>
      </Box>
    </LayoutGroup>
  );

  if (typeof document === 'undefined') return navTree;

  return createPortal(navTree, document.body);
}
